import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

// Create a context for the dev mode authentication
export const DevModeAuthContext = createContext(null);

// Custom hook to use either Auth0 or dev mode authentication
export const useAuth = () => {
  const devModeAuth = useContext(DevModeAuthContext);
  const auth0 = useAuth0();
  
  // If dev mode is active and we have mock user data, use that
  // Otherwise fall back to Auth0
  return devModeAuth?.isDevMode ? devModeAuth : auth0;
};

const Auth0ProviderWithHistory = ({ children }) => {
  const navigate = useNavigate();
  const [isDevMode, setIsDevMode] = useState(false);
  const [mockUser, setMockUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  const devModeEnabled = process.env.REACT_APP_DEV_MODE === 'true';

  useEffect(() => {
    // Check if we should enable dev mode
    if (devModeEnabled && process.env.NODE_ENV === 'development') {
      console.warn('🔧 Development mode active: Auth0 authentication is bypassed');
      setIsDevMode(true);
      
      // Create a mock user
      setMockUser({
        sub: 'dev|mock-user-123',
        email: 'dev@example.com',
        name: 'Development User',
        nickname: 'DevUser',
        picture: 'https://via.placeholder.com/150',
        email_verified: true,
        given_name: 'Development',
        family_name: 'User',
        locale: 'en-US',
        updated_at: new Date().toISOString()
      });
      
      setIsAuthenticated(true);
    }
  }, [devModeEnabled]);

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  // Mock functions for dev mode
  const loginWithRedirect = () => {
    console.log('Dev mode: loginWithRedirect called');
    setIsAuthenticated(true);
  };

  const logout = () => {
    console.log('Dev mode: logout called');
    setIsAuthenticated(false);
  };

  const getAccessTokenSilently = async () => {
    console.log('Dev mode: getAccessTokenSilently called');
    return 'mock-access-token-for-development';
  };

  // If dev mode is active, provide the mock auth context
  if (isDevMode) {
    const devAuthValue = {
      isDevMode,
      isAuthenticated,
      user: mockUser,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      isLoading: false,
      error: null
    };

    return (
      <DevModeAuthContext.Provider value={devAuthValue}>
        {children}
      </DevModeAuthContext.Provider>
    );
  }

  // If Auth0 credentials are missing, show error
  if (!(domain && clientId)) {
    return (
      <div className="auth-error">
        <h2>Configuration Error</h2>
        <p>Auth0 configuration is missing. Please check your environment variables:</p>
        <ul>
          <li>REACT_APP_AUTH0_DOMAIN</li>
          <li>REACT_APP_AUTH0_CLIENT_ID</li>
          <li>REACT_APP_AUTH0_AUDIENCE (optional)</li>
        </ul>
        <p>Alternatively, enable development mode by setting REACT_APP_DEV_MODE=true in your .env file.</p>
      </div>
    );
  }

  // Otherwise, use the real Auth0 provider
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        ...(audience ? { audience } : null),
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
